import {
  AcquisitionChannel,
  Currency,
  getMetricAgentViewsByCriteria,
  LoanStatusExternal,
  MetricAgentView,
  NotFoundException,
} from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';

import { AgentIdFilter } from '@/components/modules/Metrics/shared/entities/Filter/AgentIdFilter';
import { ChannelIdFilter } from '@/components/modules/Metrics/shared/entities/Filter/ChannelIdFilter';
import { CurrencyFilter } from '@/components/modules/Metrics/shared/entities/Filter/CurrencyFilter';
import { EndDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/EndDateFilter';
import { LoanStatusExternalFilter } from '@/components/modules/Metrics/shared/entities/Filter/LoanStatusExternalFilter';
import { MerchantIdFilter } from '@/components/modules/Metrics/shared/entities/Filter/MerchantIdFilter';
import { StartDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/StartDateFilter';
import { QueryBuilder } from '@/components/modules/Metrics/shared/entities/QueryBuilder';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';

interface UseMetricAgentViewsProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal[];
  merchantIds: string[];
  channelId?: string;
  agentId?: string;
  acquisitionChannel?: AcquisitionChannel[];
}

export const useMetricMerchantChannelAgentViews = ({
  currency,
  timeInterval,
  loanStatusExternal,
  merchantIds,
  channelId,
  agentId,
}: UseMetricAgentViewsProps) => {
  const {
    state: { metricAgentQueryBuilder: queryBuilder, metricAgentViews, metricsRefreshToken },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const [hasRequested, setHasRequested] = useState(false);
  const [filteredMetrics, setFilteredMetrics] = useState<MetricAgentView[] | undefined>(undefined);
  const lastToken = useRef(metricsRefreshToken);

  const setQueryBuilder = (queryBuilder: QueryBuilder) =>
    updateOperationsContext({ metricAgentQueryBuilder: queryBuilder });

  useEffect(() => {
    const force = lastToken.current !== metricsRefreshToken;
    lastToken.current = metricsRefreshToken;
    getMetricAgentViews(force);
  }, [timeInterval, loanStatusExternal, metricsRefreshToken]);

  const getMetricAgentViews = async (force = false) => {
    const newQueryBuilder = new QueryBuilder([
      new CurrencyFilter([currency]),
      new StartDateFilter([timeInterval]),
      new EndDateFilter([timeInterval]),
      new LoanStatusExternalFilter(loanStatusExternal),
      new MerchantIdFilter(merchantIds),
      ...(channelId ? [new ChannelIdFilter([channelId])] : []),
      ...(agentId ? [new AgentIdFilter([agentId])] : []),
    ]);

    try {
      if (force || !queryBuilder || queryBuilder.needsRequest(newQueryBuilder)) {
        const query = newQueryBuilder.merge(queryBuilder).createQuery();
        if (!force) setHasRequested(false);
        const newMetrics = await getMetricAgentViewsByCriteria({ query });
        const filteredMetrics = newQueryBuilder.filterMetrics(newMetrics);
        setFilteredMetrics(filteredMetrics);
        setQueryBuilder(newQueryBuilder.merge(queryBuilder));
        updateOperationsContext({ metricAgentViews: newMetrics });
      } else {
        const filteredMetrics = newQueryBuilder.filterMetrics(metricAgentViews);
        setFilteredMetrics(filteredMetrics);
      }
    } catch (error) {
      if (error instanceof NotFoundException) {
        setFilteredMetrics([]);
        updateOperationsContext({ metricAgentViews: [] });
      }
    } finally {
      setHasRequested(true);
    }
  };

  return { filteredMetrics, hasRequested };
};
